/* eslint-disable react/prop-types */
import React, { useEffect, useState, useRef, forwardRef } from 'react'
import { MaterialReactTable } from 'material-react-table'
import { Typography, CircularProgress, Box } from '@mui/material'
import { useTranslation } from 'react-i18next'
import FileDownloadIcon from '@mui/icons-material/FileDownload'
import { Visibility } from '@material-ui/icons'
import { XDivider } from 'irmlib/styles'
import Papa from 'papaparse'
import { Button, TableContainer, Dialog, DialogContent, DialogContentText, DialogActions } from '@material-ui/core'
import { useMergeState } from 'irmlib/hooks'
// import { useNats } from '../../../providers/NatsProvider.jsx'
import { useGetNatsImage } from '../../../services/nats/useGetNatsImage'
import * as constants from '../../../irmlib/src/common/constants.js'

const initialState = {
  selectedImage: null,
  loadingImage: false,
  imageError: false
}

const TemplateMaterialTable = forwardRef((props, ref) => {
  const { t } = useTranslation()
  const [, setState, getState] = useMergeState(initialState)
  const [rowSelected, setRowSelected] = useState({})
  const [selectedRow, setSelectedRow] = useState(null)
  // const { natsService } = useNats()
  const { getImgFromNats } = useGetNatsImage()
  // const imageCache = useRef(new Map())
  const isDialogOpenRef = useRef(false)


  useEffect(() => {
    onSelectionChange()
  }, [rowSelected])

  const loadImageFromNats = async (imgReference) => {
    if (!imgReference) return null
    
    return await getImgFromNats(
      constants.LOCAL_NATS_SERVER,
      constants.LOCAL_AI_REF_IMG,
      imgReference,
      true // Enable caching
    )

    // --- OLD CODE ---
    // // Check cache first
    // if (imageCache.current.has(imgReference)) {
    //   return imageCache.current.get(imgReference)
    // }
    // 
    // // For non-NATS images (URLs), return immediately and cache
    // if (!imgReference.includes('nats')) {
    //   if (imgReference.startsWith('http://') || 
    //       imgReference.startsWith('https://') || 
    //       imgReference.startsWith('data:')) {
    //     imageCache.current.set(imgReference, imgReference)
    //     return imgReference
    //   }
    //   return null
    // }
    // 
    // // Check if it's a NATS image
    // if (imgReference.includes('nats')) {
    //   try {
    //     const imageUrl = await natsService.getImgFromNats(
    //       constants.LOCAL_NATS_SERVER, 
    //       constants.LOCAL_AI_REF_IMG, 
    //       imgReference
    //     )
    //     if (imageUrl) {
    //       imageCache.current.set(imgReference, imageUrl)
    //       return imageUrl
    //     }
    //   } catch (error) {
    //     console.error('Error loading image from NATS:', error)
    //     return null
    //   }
    // }
    // 
    // return null
  }

  const handleImageClick = async (imgReference) => {
    if (!imgReference) return
    
    // --- OLD CODE ---
    // // Check cache first
    // if (imageCache.current.has(imgReference)) {
    //   const cachedImage = imageCache.current.get(imgReference)
    //   if (cachedImage) {
    //     isDialogOpenRef.current = true
    //     setState({ selectedImage: cachedImage, loadingImage: false, imageError: false })
    //     return
    //   }
    // }
    
    isDialogOpenRef.current = true
    // Open dialog immediately with loading state
    setState({ loadingImage: true, selectedImage: null, imageError: false })
    
    try {
      const imageUrl = await loadImageFromNats(imgReference)
      if (isDialogOpenRef.current) {
        if (imageUrl) {
          setState({ selectedImage: imageUrl, loadingImage: false, imageError: false })
        } else {
          setState({ selectedImage: null, loadingImage: false, imageError: true })
        }
      }
    } catch (error) {
      console.error('Error loading image:', error)
      if (isDialogOpenRef.current) {
        setState({ selectedImage: null, loadingImage: false, imageError: true })
      }
    } finally {
      if (isDialogOpenRef.current) {
        setState({ loadingImage: false })
      }
    }
  }

  const columns = [                                                                
    {
      header: props?.fromMultiReqs ? t('tableColumns.templateId') : t('tableColumns.templateId'),
      accessorKey: 'requestId',
      grow: true,
    },
    {
      header: t('tableColumns.customTemplateName'),
      accessorKey: 'customTemplateName',
      grow: true,
      minSize: 250,
    },
    {
      header: t('templates.createdBy'),
      accessorKey: 'createdBy',
      grow: true,
    },
    {
      header: t('templates.createdOn'),
      accessorKey: 'createdOn',
      grow: true,
    },
    {
      header: t('tableColumns.templateFields'),
      accessorKey: 'requestFields',
      grow: true,
      minSize: 500,
    },
    {
      header: t('tableColumns.imgReference'),
      accessorKey: 'imgReference',
      grow: true,
      Cell: ({ row }) => (
        row.original.imgReference 
          ? (
            <Visibility
              style={{ cursor: 'pointer', fontSize: 32 }}
              onClick={() => {
                if (props.onImageClick) {
                  props.onImageClick(row.original.imgReference)
                } else {
                  handleImageClick(row.original.imgReference)
                }
              }}
            />
            )
          : null
      ),
    }
  ]

  const exportCSV = (rows, filename) => {
    const csvString = Papa.unparse(rows)
    const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.setAttribute('href', url)
    link.setAttribute('download', filename)
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }
  
  const getRows = (table, isSelected = false) => {
    const rows = isSelected
      ? table.getSelectedRowModel().rows.map(row => row.original)
      : table.getRowModel().rows.map(row => row.original)
  
    return rows.map(row => {
      return columns.reduce((acc, col) => {
        acc[col.header] = row[col.accessorKey]
        return acc
      }, {})
    })
  }

  const getAllRows = (table) => {
    const rows = table.getCoreRowModel().rows.map(row => row.original)
  
    return rows.map(row => {
      return columns.reduce((acc, col) => {
        acc[col.header] = row[col.accessorKey]
        return acc
      }, {})
    })
  }

  const handleExport = (table) => {
    const allData = getAllRows(table, true)
    exportCSV(allData, 'data.csv')
  }
  
  const handleExportSelected = (table) => {
    const rows = getRows(table, true)
    exportCSV(rows, 'selected_data.csv')
  }
  
  const handleExportPerPage = (table) => {
    const rowsPerPage = getRows(table)
    exportCSV(rowsPerPage, 'data_perPage.csv')
  }

  const handleClose = () => {
    isDialogOpenRef.current = false
    setState({ selectedImage: null, loadingImage: false, imageError: false })
  }

  const ImageContainer = () => {
    const isOpen = getState().loadingImage || !!getState().selectedImage || getState().imageError
    
    if (!isOpen) return null
    
    return (
      <Dialog 
        open={isOpen} 
        onClose={handleClose}
        maxWidth="sm"
      >
        <DialogContent>
          <DialogContentText component="div">
            {getState().loadingImage && !getState().selectedImage && !getState().imageError
              ? <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: '400px',
                minWidth: '400px',
                gap: 2
              }}>
                <CircularProgress size={40} />
                <Typography variant="body2" color="text.secondary">
                  {t('Search.loading')}
                </Typography>
              </Box>
              : getState().selectedImage && !getState().imageError
                ? <img
                  src={getState().selectedImage}
                  alt="Reference Image"
                  style={{
                    maxWidth: '100%',
                    maxHeight: '100%',
                    display: 'block',
                    margin: '0 auto'
                  }}
                  onError={() => {
                    setState({ selectedImage: null, loadingImage: false, imageError: true })
                  }}
                />
                : <Box sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  minHeight: '400px',
                  minWidth: '400px',
                  gap: 2
                }}>
                  <Typography variant="body2" color="text.secondary">
                    {t('smartCatalog.imageNotAvailable')}
                  </Typography>
                </Box>}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button color="primary" onClick={handleClose}>
            {t('dataEntry.close')}
          </Button>
        </DialogActions>
      </Dialog>
    )
  }

  const onSelectionChange = () => {
    if (rowSelected.length !== 0) {
      props.handleSelectedRows(rowSelected?.original?.requestId)
    }
  }
  
  let tableTitle = t('routeTitles.smartCatalogRequest')
  if (props.isSmartCatalogLocal) {
    tableTitle = props.fromMultiReqs
      ? t('smartCatalogLocal.smartCatalogLocalTemplates')
      : t('routeTitles.smartCatalogLocalRequest')
  } else if (props.fromMultiReqs) {
    tableTitle = t('smartCatalog.smartCatalogTemplates')
  }

  return (
    <>
      <TableContainer>
        <div style={{ display: 'flex', flexDirection: 'column', marginLeft: '22px' }}>
          <Typography variant="h6" gutterBottom style={{ color: '#58595B' }}>
            {tableTitle}
          </Typography></div>
        <MaterialReactTable
          muiTableHeadCellProps={{
            sx: {
              color: '#58595B',
              fontWeight: 'bold',
            },
          }}
          columns={columns}
          // options={options}
          data={props.data}
          enableRowSelection
          enableExport={true}
          enableGlobalFilter
          enableColumnFilter
          enableColumnSorting
          enableGrouping={true}
          enableColumnResizing={true}
          positionToolbarAlertBanner='none'
          state={{ rowSelection: selectedRow ? { [selectedRow]: true } : {} }}
          muiSelectCheckboxProps={({ row }) => ({
            checked: selectedRow === row.id,
            onChange: () => {
              setSelectedRow(selectedRow === row.id ? null : row.id)
              setRowSelected(row)
            }
          })}
          handleSelectedRows={props.handleSelectedRows}
          initialState={{ columnVisibility: { imgReference: true, modify: true }, density: 'compact' }}

          renderTopToolbarCustomActions={({ table }) => {
            const isDisabled = !table.getIsSomeRowsSelected()
            const isNoData = props.data?.length === 0
            const isNoPageData = !props.data?.length > 4
            return (
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <Button
                  disabled={isNoData}
                  style={{
                    color: isNoData ? '#C8C8C8' : '#58595B',
                    fontWeight: 'bold'
                  }}
                  onClick={() => handleExport(table)}
                  endIcon={<FileDownloadIcon />}>{t('smartCatalog.exportAllData')}
                </Button>
                <XDivider width='10px' />
                <Button
                  onClick={() => handleExportSelected(table)}
                  disabled={isDisabled}
                  style={{
                    color: isDisabled ? '#C8C8C8' : '#58595B',
                    fontWeight: 'bold'
                  }}
                  endIcon={<FileDownloadIcon />}>{t('smartCatalog.exportSelectedData')}
                </Button>
                <XDivider width='10px' />
                <Button
                  disabled={isNoPageData}
                  style={{
                    color: isNoPageData ? '#C8C8C8' : '#58595B',
                    fontWeight: 'bold'
                  }}
                  onClick={() => handleExportPerPage(table)}
                  endIcon={<FileDownloadIcon />}>{t('smartCatalog.exportPageData')}
                </Button>
              </div>
            )
          }}
          tableFor='smart_catalog_view_requests'
        />
      </TableContainer>
      <ImageContainer />
    </>
  )
})

TemplateMaterialTable.displayName = 'TemplateMaterialTable'

export default TemplateMaterialTable